<template>
  <div class="bg-pattern-gray client-view-wrap">
    <div class="dashboard-body-content d-flex justify-content-center align-items-center p-5">
      <div class="client-columns">
        <div class="detail-wrap">
          <div class="card card-form">
            <div class="card-header">
              <div class="card-title">Client Details</div>
            </div>
            <div class="card-body">
              <div class="detail-head">
                <div class="initials-box" aria-hidden="true">
                  <span v-if="!initials" class="placeholder-text">No Name</span>
                  <template v-else>{{ initials }}</template>
                </div>
              </div>

              <div class="detail-list">
                <div class="detail-row" v-for="field in fields" :key="field.key">
                  <div class="detail-label">{{ field.label }}</div>
                  <div class="detail-value" :class="{ empty: isEmpty(field.key), note: field.key === 'notes' }">
                    <a v-if="field.key === 'email' && !isEmpty('email')" :href="'mailto:' + client.email">
                      {{ client.email }}
                    </a>
                    <a v-else-if="field.key === 'website' && !isEmpty('website')" :href="websiteHref" target="_blank"
                      rel="noopener">
                      {{ client.website }}
                    </a>
                    <template v-else>{{ displayValue(field.key) }}</template>
                  </div>
                </div>
              </div>

              <div class="form-footer">
                <button type="button" class="btn btn-cancel"
                  @click="$router.push({ name: 'list-clients' })">Back</button>
                <button type="button" class="btn btn-next"
                  @click="$router.push({ name: 'edit-client', params: { id: $route.params.id } })">Edit</button>
              </div>
            </div>
          </div>
        </div>

        <AddressMap :street1="mapField('postal_address')" :state="mapField('state')" :zip="mapField('zip')"
          :country="mapField('country')" />
      </div>
    </div>
  </div>
</template>

<script>
import AddressMap from "../partials/AddressMap.vue";

export default {
  name: "ViewClient",
  components: { AddressMap },
  data() {
    return {
      client: {},
      fields: [
        { key: "full_name", label: "Name/Company Name" },
        { key: "business_name", label: "Business Name" },
        { key: "email", label: "Email address" },
        { key: "phone1", label: "Phone no" },
        { key: "phone2", label: "Phone no 2" },
        { key: "website", label: "Website" },
        { key: "postal_address", label: "Postal address / Street 1" },
        { key: "street2", label: "Street 2" },
        { key: "state", label: "County/State" },
        { key: "zip", label: "Zip/Postal Code" },
        { key: "country", label: "Country" },
        { key: "notes", label: "Notes" },
      ],
    };
  },
  computed: {
    initials() {
      const name = (this.client.full_name || this.client.business_name || "").trim();
      if (!name) return "";
      return name
        .split(/\s+/)
        .map((w) => w.replace(/[^A-Za-z0-9]/g, ""))
        .filter(Boolean)
        .map((w) => w[0].toUpperCase())
        .slice(0, 2)
        .join("");
    },
    websiteHref() {
      const url = this.client.website || "";
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    },
  },
  created() {
    this.loadClientData();
  },
  methods: {
    showLoading() {
      this.loadingInstance = this.$loading({
        lock: true,
        text: "Loading",
        background: "rgba(0, 0, 0, 0.7)",
        fullscreen: true,
      });
    },
    hideLoading() {
      if (this.loadingInstance) {
        this.loadingInstance.close();
      }
    },
    // Same emptiness rule as the detail rows, so "N/A" never reaches the geocoder.
    mapField(key) {
      return this.isEmpty(key) ? "" : String(this.client[key]);
    },
    isEmpty(key) {
      const value = this.client[key];
      return value === null || value === undefined || value === "" || value === "N/A";
    },
    displayValue(key) {
      return this.isEmpty(key) ? "—" : this.client[key];
    },
    async loadClientData() {
      this.showLoading();
      try {
        const res = await this.$store.dispatch("client/getEditClientData", {
          clientId: parseInt(this.$route.params.id),
        });
        if (res.status == 1) {
          const data = res.response;
          this.client = {
            ...data,
            postal_address: data.postal_address || data.street1 || "",
          };
        } else {
          throw new Error("Failed to load client");
        }
      } catch (err) {
        console.log(err);
      } finally {
        this.hideLoading();
      }
    },
  },
};
</script>

<style scoped>
.client-view-wrap {
  --teal: #18798c;
  --panel: #f0f1f2;
  --ink: #2c3038;
  --muted: #8a909a;
  --line: #d9dde2;
  --green: #2da44e;
  --green-dark: #248a42;
  --radius: 14px;
  --shadow: 0 6px 24px rgba(20, 30, 50, 0.08);

  color: var(--ink);
}

/* Details card on the left, location card beside it when a zip is present. */
.client-columns {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 28px;
  width: 100%;
}

.detail-wrap {
  width: 100%;
  max-width: 560px;
}

.detail-wrap .card {
  background: transparent;
  border-radius: var(--radius);
  box-shadow: none;
  overflow: visible;
  border: none;
}

.detail-wrap .card-header {
  background: transparent;
  border: none;
  padding: 0;
  display: flex;
  justify-content: flex-start;
}

.detail-wrap .card-form .card-header .card-title,
.detail-wrap .card-header .card-title,
.detail-wrap .card-title {
  background: var(--teal) !important;
  background-color: var(--teal) !important;
  color: #fff !important;
  height: 55px;
  margin: 0;
  padding: 8px 24px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  font-weight: 700;
  border-radius: 8px 8px 0 0;
  width: fit-content;
  min-width: 180px;
}

.detail-wrap .card-body {
  background: var(--panel);
  border-radius: 0 8px 8px 8px;
  box-shadow: var(--shadow);
  padding: 26px 70px 28px;
}

.detail-head {
  display: flex;
  align-items: flex-start;
  justify-content: flex-end;
  gap: 12px;
}

.initials-box {
  flex-shrink: 0;
  width: 64px;
  height: 64px;
  border: 1px solid #c7ccd2;
  background: #e9ebee;
  color: var(--teal);
  border-radius: 8px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 28px;
  font-weight: 700;
  letter-spacing: 1px;
  user-select: none;
}

.initials-box .placeholder-text {
  font-size: 12px;
  font-weight: 600;
  color: var(--muted);
  letter-spacing: 0;
  text-align: center;
  padding: 0 6px;
}

.detail-list {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  overflow: hidden;
}

.detail-row {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 11px 14px;
  border-bottom: 1px solid var(--line);
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-label {
  flex: 0 0 44%;
  max-width: 44%;
  font-size: 13px;
  font-weight: 600;
  color: var(--muted);
}

.detail-value {
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: var(--ink);
  word-break: break-word;
}

.detail-value.empty {
  color: #b6bcc6;
}

.detail-value.note {
  white-space: pre-wrap;
}

.detail-value a {
  color: var(--teal);
  text-decoration: none;
}

.detail-value a:hover {
  text-decoration: underline;
}

.btn {
  cursor: pointer;
}

.detail-wrap .form-footer,
.form-footer {
  margin-top: 50px !important;
  margin-bottom: 50px !important;
  padding-top: 0 !important;
  display: flex !important;
  flex-flow: row nowrap !important;
  align-items: center !important;
  justify-content: flex-end !important;
  gap: 12px !important;
  width: 100% !important;
}

.btn-cancel {
  background: #fff;
  border: 1px solid var(--line);
  border-radius: 8px;
  padding: 10px 22px;
  font-size: 14px;
  font-weight: 600;
  color: var(--ink);
}

.btn-cancel:hover {
  background: #f4f5f6;
  color: var(--ink);
}

.btn-next {
  background: var(--green);
  border: none;
  border-radius: 8px;
  padding: 10px 30px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  transition: background .15s ease;
}

.btn-next:hover {
  background: var(--green-dark);
  color: #fff;
}

@media (max-width: 991px) {
  .client-columns {
    flex-direction: column;
    align-items: center;
  }
}

@media (max-width: 540px) {
  .detail-wrap .card-body {
    padding: 20px 20px 22px;
  }

  .detail-head {
    flex-direction: column;
  }

  .initials-box {
    width: 100%;
    height: 56px;
    font-size: 24px;
  }

  .detail-row {
    flex-direction: column;
    gap: 2px;
  }

  .detail-label {
    flex: 0 0 auto;
    max-width: 100%;
  }

  .detail-wrap .card-title {
    height: 48px;
    font-size: 16px;
    padding: 8px 20px;
  }
}
</style>
